import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './AgencyCustomerManagement.css';
import { getCustomers, createCustomer, getNextCustomerId } from '../../services/customersService';
import api from '../../config/api';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { getStates, getDistrictsByState } from '../../services/locationService';

const AgencyCustomerManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [customerNameDropdownOpen, setCustomerNameDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [customerNameSearch, setCustomerNameSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  // Form dropdown states for searchable State and City
  const [formStateDropdownOpen, setFormStateDropdownOpen] = useState(false);
  const [formCityDropdownOpen, setFormCityDropdownOpen] = useState(false);
  const [formStateSearch, setFormStateSearch] = useState('');
  const [formCitySearch, setFormCitySearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    panNumber: '',
    aadharNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '', // Will store district name for display
    cityId: null, // Will store district ID for API
    pincode: '',
    state: '', // Will store state name for display
    stateId: null, // Will store state ID for API
    country: 'India',
    emailId: '',
    mobileNumber: '',
    alternateContactNumber: '',
    photo: null,
    photoPreview: null
  });
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successCustomerName, setSuccessCustomerName] = useState('');
  const [successModalType, setSuccessModalType] = useState('add'); // 'add' or 'update'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedCustomerInfo, setDeletedCustomerInfo] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSampleCSVModal, setShowSampleCSVModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importValidCustomers, setImportValidCustomers] = useState([]);
  const [importInvalidRows, setImportInvalidRows] = useState([]);
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importSkippedCount, setImportSkippedCount] = useState(0);

  const customerNameDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);
  const formStateDropdownRef = useRef(null);
  const formCityDropdownRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentAgencyId, setCurrentAgencyId] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  // Filter districts (separate from form districts)
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [loadingFilterDistricts, setLoadingFilterDistricts] = useState(false);

  // Get current agency ID from localStorage
  useEffect(() => {
    const savedAgency = localStorage.getItem('agencyProfile');
    if (savedAgency) {
      try {
        const agency = JSON.parse(savedAgency);
        const agencyId = agency.id || agency.agency_id || null;
        if (agencyId) {
          console.log('Agency Customer Management: Loaded agency ID:', agencyId);
          setCurrentAgencyId(agencyId);
        } else {
          console.warn('Agency Customer Management: No agency ID found in agency profile');
          setCurrentAgencyId(null);
        }
      } catch (error) {
        console.error('Error loading agency profile:', error);
        setCurrentAgencyId(null);
      }
    } else {
      console.warn('Agency Customer Management: No agency profile found in localStorage');
      setCurrentAgencyId(null);
    }
  }, []);

  // Map API response to component format
  const mapCustomerFromAPI = (apiCustomer = {}) => {
    const rawId =
      apiCustomer.customer_code ||
      apiCustomer.customer_id ||
      apiCustomer.id ||
      apiCustomer.id?.toString() ||
      '';

    return {
      id: rawId?.toString() || apiCustomer.id?.toString() || '',
      customerCode:
        apiCustomer.customer_code ||
        apiCustomer.customer_id ||
        apiCustomer.id?.toString() ||
        '',
      customerName: apiCustomer.customer_name || apiCustomer.name || '',
      panNumber: apiCustomer.pan_number || '',
      aadharNumber: apiCustomer.aadhar_number || '',
      addressLine1: apiCustomer.address_line1 || '',
      addressLine2: apiCustomer.address_line2 || '',
      // Use district_name from JOIN, fallback to city for backward compatibility
      city: apiCustomer.district_name || apiCustomer.city || '',
      cityId: apiCustomer.city_id || apiCustomer.district_id || null,
      pincode: apiCustomer.pincode || apiCustomer.zip_code || '',
      // Use state_name from JOIN, fallback to state for backward compatibility
      state: apiCustomer.state_name || apiCustomer.state || '',
      stateId: apiCustomer.state_id || null,
      country: apiCustomer.country || 'India',
      emailId: apiCustomer.email_id || apiCustomer.email || '',
      mobileNumber: apiCustomer.mobile_number || apiCustomer.phone || '',
      alternateContactNumber:
        apiCustomer.alternate_contact_number || apiCustomer.alternate_phone || '',
      photo: apiCustomer.photo_url || null,
      agencyId: apiCustomer.agency_id || null
    };
  };

  const mapCustomerToAPI = (customer) => {
    const payload = {
      customer_code: customer.customerId || customer.customerCode,
      customer_name: customer.customerName,
      pan_number: customer.panNumber,
      aadhar_number: customer.aadharNumber,
      address_line1: customer.addressLine1,
      address_line2: customer.addressLine2,
      city: customer.cityId || customer.city, // Send district ID if available, otherwise name
      pincode: customer.pincode,
      state: customer.stateId || customer.state, // Send state ID if available, otherwise name
      country: customer.country || 'India',
      email_id: customer.emailId,
      password: customer.password || undefined,
      mobile_number: customer.mobileNumber,
      alternate_contact_number: customer.alternateContactNumber,
      photo: customer.photo || null,
      agency_id: customer.agencyId || null,
      // Always include state_id and city_id (even if null) - backend expects these
      state_id: customer.stateId || null,
      city_id: customer.cityId || null // Backend uses city_id for district
    };
    
    console.log('Agency mapCustomerToAPI payload:', payload);
    return payload;
  };

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (searchQuery) params.search = searchQuery;
      if (customerNameFilter) params.customer_name = customerNameFilter;
      if (cityFilter) params.city = cityFilter;
      if (stateFilter) params.state = stateFilter;

      // Filter by current agency ID for agency users
      if (currentAgencyId) {
        params.agency_id = currentAgencyId;
        console.log('Agency Customer Management: Fetching customers with agency_id:', currentAgencyId);
      }

      const response = await getCustomers(params);

      let customersData = [];

      if (Array.isArray(response)) {
        customersData = response;
      } else if (Array.isArray(response?.data)) {
        customersData = response.data;
      } else if (Array.isArray(response?.data?.items)) {
        customersData = response.data.items;
      } else if (Array.isArray(response?.items)) {
        customersData = response.items;
      } else if (Array.isArray(response?.data?.data)) {
        customersData = response.data.data;
      } else if (Array.isArray(response?.data?.customers)) {
        customersData = response.data.customers;
      }

      if (!Array.isArray(customersData)) {
        customersData = [];
      }

      const pagination =
        response?.pagination ||
        response?.meta ||
        response?.data?.pagination ||
        response?.data?.meta ||
        response?.data?.data?.pagination ||
        {};

      let mappedCustomers = customersData.map(mapCustomerFromAPI);
      
      // Apply client-side agency filter as fallback (in case backend doesn't filter by agency_id)
      if (currentAgencyId && mappedCustomers.length > 0) {
        const beforeFilter = mappedCustomers.length;
        mappedCustomers = mappedCustomers.filter(c => {
          const customerAgencyId = c.agencyId || c.raw?.agency_id;
          const matches = customerAgencyId && String(customerAgencyId) === String(currentAgencyId);
          if (!matches && customerAgencyId) {
            console.log('Agency Customer Management: Filtered out customer:', c.id, 'agencyId:', customerAgencyId, 'expected:', currentAgencyId);
          }
          return matches;
        });
        console.log('Agency Customer Management: After agency filter:', mappedCustomers.length, 'customers (was', beforeFilter, 'before filter)');
      }

      setCustomers(mappedCustomers);
      setTotalCount(pagination.total || pagination.count || mappedCustomers.length);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.message || 'Failed to fetch customers. Please try again.');
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, customerNameFilter, cityFilter, stateFilter, currentAgencyId]);

  useEffect(() => {
    if (currentAgencyId !== null && currentAgencyId !== undefined) {
      fetchCustomers();
    } else if (currentAgencyId === null) {
      // If agency ID is explicitly null (after checking localStorage), show empty state
      setCustomers([]);
      setTotalCount(0);
      setLoading(false);
    }
  }, [fetchCustomers, currentAgencyId]);

  // Fetch states on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoadingStates(true);
        const response = await getStates();
        console.log('Agency - States API response:', response);
        
        // Try multiple response formats
        let statesData = [];
        if (Array.isArray(response)) {
          statesData = response;
        } else if (Array.isArray(response?.data)) {
          statesData = response.data;
        } else if (Array.isArray(response?.items)) {
          statesData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          statesData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          statesData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          statesData = response.data;
        }
        
        console.log('Agency - Parsed states data:', statesData);
        setStates(statesData);
      } catch (err) {
        console.error('Error fetching states:', err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  // Fetch districts when state changes in form
  useEffect(() => {
    const loadDistricts = async () => {
      console.log('🔍 Agency loadDistricts triggered. formData.state:', formData.state, 'states.length:', states.length);
      
      if (!formData.state) {
        console.log('❌ No state selected, clearing districts');
        setDistricts([]);
        return;
      }
      
      if (states.length === 0) {
        console.log('❌ States array is empty, cannot find state ID');
        setDistricts([]);
        return;
      }
      
      try {
        // Find state ID from state name
        console.log('🔍 Looking for state:', formData.state);
        console.log('🔍 Available states:', states.map(s => ({ name: s.name || s.state_name || s.state, id: s.id || s.state_id })));
        
        const selectedState = states.find(s => {
          const stateName = (s.name || s.state_name || s.state || '').toLowerCase().trim();
          const formStateName = formData.state.toLowerCase().trim();
          return stateName === formStateName;
        });
        
        console.log('🔍 Selected state found:', selectedState);
        
        if (!selectedState) {
          console.error('❌ State not found in states array. Selected:', formData.state);
          console.error('Available state names:', states.map(s => s.name || s.state_name || s.state));
          setDistricts([]);
          return;
        }
        
        const stateId = selectedState.id || selectedState.state_id;
        console.log('🔍 Extracted state_id:', stateId);
        
        if (!stateId) {
          console.error('❌ State ID not found in selected state object:', selectedState);
          setDistricts([]);
          return;
        }
        
        console.log('✅ Calling districts API with state_id:', stateId);
        setLoadingDistricts(true);
        const response = await getDistrictsByState(stateId);
        console.log('Agency - Districts API response for state', formData.state, ':', response);
        
        // Try multiple response formats
        let districtsData = [];
        if (Array.isArray(response)) {
          districtsData = response;
        } else if (Array.isArray(response?.data)) {
          districtsData = response.data;
        } else if (Array.isArray(response?.items)) {
          districtsData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          districtsData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          districtsData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          districtsData = response.data;
        }
        
        console.log('Agency - Parsed districts data:', districtsData);
        setDistricts(districtsData);
      } catch (err) {
        console.error('Error fetching districts:', err);
        setDistricts([]);
      } finally {
        setLoadingDistricts(false);
      }
    };
    loadDistricts();
  }, [formData.state, states]);

  // Mock data based on the image - REMOVED, using API data now
  const initialCustomers = [
    {
      id: 'CUST-001',
      customerCode: 'CUST-001',
      customerName: 'Reliance Industries Ltd.',
      city: 'Mumbai',
      state: 'Maharashtra',
      mobileNumber: '9876543210',
      emailId: 'contact@reliance.com',
      addressLine1: 'Maker Chambers IV, 222...',
      addressLine2: 'Mumbai Central, Mumbai -...',
      aadharNumber: 'XXXX-XXXX-1234'
    },
    {
      id: 'CUST-002',
      customerCode: 'CUST-002',
      customerName: 'Tata Motors Ltd.',
      city: 'Pune',
      state: 'Maharashtra',
      mobileNumber: '9876543211',
      emailId: 'contact@tatamotors.com',
      addressLine1: 'Bombay House, 24 Homi...',
      addressLine2: 'Fort, Mumbai - 400001',
      aadharNumber: 'XXXX-XXXX-2345'
    },
    {
      id: 'CUST-003',
      customerCode: 'CUST-003',
      customerName: 'Infosys Ltd.',
      city: 'Bangalore',
      state: 'Karnataka',
      mobileNumber: '9876543212',
      emailId: 'contact@infosys.com',
      addressLine1: 'Electronics City, Hosur Road',
      addressLine2: 'Bangalore - 560100',
      aadharNumber: 'XXXX-XXXX-3456'
    },
    {
      id: 'CUST-004',
      customerCode: 'CUST-004',
      customerName: 'Wipro Ltd.',
      city: 'Bangalore',
      state: 'Karnataka',
      mobileNumber: '9876543213',
      emailId: 'contact@wipro.com',
      addressLine1: 'Doddakannelli, Sarjapur Road',
      addressLine2: 'Bangalore - 560035',
      aadharNumber: 'XXXX-XXXX-4567'
    },
    {
      id: 'CUST-005',
      customerCode: 'CUST-005',
      customerName: 'Mahindra & Mahindra Ltd.',
      city: 'Mumbai',
      state: 'Maharashtra',
      mobileNumber: '9876543214',
      emailId: 'contact@mahindra.com',
      addressLine1: 'Gateway Building, Apollo...',
      addressLine2: 'Mumbai - 400001',
      aadharNumber: 'XXXX-XXXX-5678'
    },
    {
      id: 'CUST-006',
      customerCode: 'CUST-006',
      customerName: 'Larsen & Toubro Ltd.',
      city: 'Mumbai',
      state: 'Maharashtra',
      mobileNumber: '9876543215',
      emailId: 'contact@larsentoubro.com',
      addressLine1: 'L&T House, Ballard Estate',
      addressLine2: 'Mumbai - 400001',
      aadharNumber: 'XXXX-XXXX-6789'
    },
    {
      id: 'CUST-007',
      customerCode: 'CUST-007',
      customerName: 'Bharti Airtel Ltd.',
      city: 'New Delhi',
      state: 'Delhi',
      mobileNumber: '9876543216',
      emailId: 'contact@airtel.com',
      addressLine1: 'Bharti Crescent, 1 Nelson...',
      addressLine2: 'Vasant Kunj, New Delhi - 1...',
      aadharNumber: 'XXXX-XXXX-7890'
    }
  ];

  // Customers state is now managed by API - removed mock data initialization

  // Get unique values for filters
  const uniqueCustomerNames = useMemo(() => Array.from(new Set(customers.map(c => c.customerName))), [customers]);
  const uniqueCities = useMemo(() => Array.from(new Set(customers.map(c => c.city))), [customers]);
  const uniqueStates = useMemo(() => Array.from(new Set(customers.map(c => c.state))), [customers]);

  // Filter options based on search
  const filteredCustomerNames = useMemo(() => {
    if (!customerNameSearch) return uniqueCustomerNames;
    return uniqueCustomerNames.filter(name => 
      name.toLowerCase().includes(customerNameSearch.toLowerCase())
    );
  }, [uniqueCustomerNames, customerNameSearch]);

  const filteredCities = useMemo(() => {
    if (!citySearch) return uniqueCities;
    return uniqueCities.filter(city => 
      city.toLowerCase().includes(citySearch.toLowerCase())
    );
  }, [uniqueCities, citySearch]);

  // Filtered states for filter dropdown (from API)
  const filteredFilterStates = useMemo(() => {
    if (!stateSearch) return states;
    const searchLower = stateSearch.toLowerCase();
    return states.filter(s => {
      const stateName = (s.name || s.state_name || s.state || '').toLowerCase();
      return stateName.includes(searchLower);
    });
  }, [states, stateSearch]);

  // Filtered cities/districts for filter dropdown (from API, based on selected state)
  const filteredFilterCities = useMemo(() => {
    if (!citySearch) return filterDistricts;
    const searchLower = citySearch.toLowerCase();
    return filterDistricts.filter(d => {
      const districtName = (d.name || d.district_name || d.district || '').toLowerCase();
      return districtName.includes(searchLower);
    });
  }, [filterDistricts, citySearch]);

  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesSearch = searchQuery === '' || 
        customer.customerCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.mobileNumber.includes(searchQuery);
      
      const matchesCustomerName = customerNameFilter === '' || customer.customerName === customerNameFilter;
      const matchesCity = cityFilter === '' || customer.city === cityFilter;
      const matchesState = stateFilter === '' || customer.state === stateFilter;

      return matchesSearch && matchesCustomerName && matchesCity && matchesState;
    });
  }, [customers, searchQuery, customerNameFilter, cityFilter, stateFilter]);

  // Use API pagination - customers already contains the current page's data
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const pageStart = (currentPage - 1) * itemsPerPage;
  const current = customers; // API already returns paginated data

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerNameDropdownRef.current && !customerNameDropdownRef.current.contains(event.target)) {
        setCustomerNameDropdownOpen(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setCityDropdownOpen(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setStateDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
      if (formStateDropdownRef.current && !formStateDropdownRef.current.contains(event.target)) {
        setFormStateDropdownOpen(false);
        setFormStateSearch('');
      }
      if (formCityDropdownRef.current && !formCityDropdownRef.current.contains(event.target)) {
        setFormCityDropdownOpen(false);
        setFormCitySearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, customerNameFilter, cityFilter, stateFilter, itemsPerPage]);

  // Auto-hide success modals
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  useEffect(() => {
    if (showImportSuccess) {
      const timer = setTimeout(() => {
        setShowImportSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showImportSuccess]);

  const clearPaging = () => setCurrentPage(1);

  const handleApplyFilters = () => {
    clearPaging();
  };

  const handleResetFilters = () => {
    setCustomerNameFilter('');
    setCityFilter('');
    setStateFilter('');
    clearPaging();
  };

  const handleExport = async () => {
    const headers = [
      'Customer Code',
      'Customer Name',
      'PAN Number',
      'Aadhar Number',
      'Address Line 1',
      'Address Line 2',
      'City',
      'Pincode',
      'State',
      'Country',
      'Email ID',
      'Mobile Number',
      'Alternate Contact Number'
    ];

    // Create workbook and worksheet using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Customers Export');

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Add data rows
    filteredCustomers.forEach(c => {
      worksheet.addRow([
        c.customerCode || c.id || '',
        c.customerName || '',
        c.panNumber || '',
        c.aadharNumber || '',
        c.addressLine1 || '',
        c.addressLine2 || '',
        c.city || '',
        c.pincode || '',
        c.state || '',
        c.country || 'India',
        c.emailId || '',
        c.mobileNumber || '',
        c.alternateContactNumber || ''
      ]);
    });

    // Set column widths to ensure headers are fully visible
    worksheet.getColumn(1).width = 18;  // Customer Code
    worksheet.getColumn(2).width = 25;  // Customer Name
    worksheet.getColumn(3).width = 18;  // PAN Number
    worksheet.getColumn(4).width = 20;  // Aadhar Number
    worksheet.getColumn(5).width = 30;  // Address Line 1
    worksheet.getColumn(6).width = 30;  // Address Line 2
    worksheet.getColumn(7).width = 20;  // City
    worksheet.getColumn(8).width = 12;  // Pincode
    worksheet.getColumn(9).width = 20;  // State
    worksheet.getColumn(10).width = 15; // Country
    worksheet.getColumn(11).width = 30; // Email ID
    worksheet.getColumn(12).width = 18; // Mobile Number
    worksheet.getColumn(13).width = 28; // Alternate Contact Number

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Customers_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Parse CSV text into array of objects using headers (handles quotes and commas)
  const parseCSV = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        if (char === '"') {
          if (inQuotes && lines[i][j + 1] === '"') {
            current += '"';
            j++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (values[idx] || '').replace(/^"|"$/g, '').replace(/""/g, '"');
      });
      rows.push(obj);
    }
    return rows;
  };

  const mapRowToCustomer = (row) => {
    const customerId = (row['Customer Code'] || '').trim() || generateCustomerId();
    return {
      id: customerId,
      customerCode: customerId,
      customerName: (row['Customer Name'] || '').trim(),
      panNumber: (row['PAN Number'] || '').trim(),
      aadharNumber: (row['Aadhar Number'] || '').trim(),
      addressLine1: (row['Address Line 1'] || '').trim(),
      addressLine2: (row['Address Line 2'] || '').trim(),
      city: (row['City'] || '').trim(),
      pincode: (row['Pincode'] || '').trim(),
      state: (row['State'] || '').trim(),
      country: (row['Country'] || 'India').trim(),
      emailId: (row['Email ID'] || '').trim(),
      mobileNumber: (row['Mobile Number'] || '').trim(),
      alternateContactNumber: (row['Alternate Contact Number'] || '').trim()
    };
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      let rows = [];

      // Check if file is Excel (.xlsx) or CSV
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Read Excel file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        
        // Convert to same format as CSV parser
        rows = jsonData.map(row => {
          const obj = {};
          Object.keys(row).forEach(key => {
            // Remove asterisk from header names if present
            const cleanKey = key.replace(/\s*\*\s*$/, '').trim();
            obj[cleanKey] = String(row[key] || '').trim();
          });
          return obj;
        });
      } else {
        // Read CSV file
        const text = await file.text();
        rows = parseCSV(text);
      }

      // Filter out empty rows (missing mandatory fields like Customer Name)
      const nonEmptyRows = rows.filter(r => (r['Customer Name'] || '').trim() !== '');

      // Validation using Customer Code/ID and Mobile Number
      const existingCodes = new Set(customers.map(c => (c.customerCode || c.id || '').toString().toLowerCase()));
      const existingMobileNumbers = new Set(customers.map(c => (c.mobileNumber || '').toLowerCase()));
      const seenInFileCodes = new Set();
      const seenInFileMobiles = new Set();
      const valid = [];
      const invalid = [];

      nonEmptyRows.forEach((r, idx) => {
        const customerName = (r['Customer Name'] || '').trim();
        const customerCode = (r['Customer Code'] || '').trim();
        const mobileNumber = (r['Mobile Number'] || '').trim();
        const codeLower = customerCode.toLowerCase();
        const mobileLower = mobileNumber.toLowerCase();

        if (!customerName) {
          invalid.push({ row: idx + 2, customerName: customerName || 'N/A', reason: 'Customer Name missing' });
        } else if (!mobileNumber) {
          invalid.push({ row: idx + 2, customerName: customerName, reason: 'Mobile Number missing' });
        } else if (customerCode && existingCodes.has(codeLower)) {
          invalid.push({ row: idx + 2, customerName: customerName, reason: 'Duplicate Customer Code' });
        } else if (customerCode && seenInFileCodes.has(codeLower)) {
          invalid.push({ row: idx + 2, customerName: customerName, reason: 'Duplicate Customer Code in file' });
        } else if (existingMobileNumbers.has(mobileLower)) {
          invalid.push({ row: idx + 2, customerName: customerName, reason: 'Duplicate Mobile Number' });
        } else if (seenInFileMobiles.has(mobileLower)) {
          invalid.push({ row: idx + 2, customerName: customerName, reason: 'Duplicate Mobile Number in file' });
        } else {
          if (customerCode) seenInFileCodes.add(codeLower);
          if (mobileNumber) seenInFileMobiles.add(mobileLower);
          valid.push(mapRowToCustomer(r));
        }
      });

      setImportFileName(file.name);
      setImportValidCustomers(valid);
      setImportInvalidRows(invalid);
      setShowImportModal(true);
    } catch (err) {
      console.error('Import error:', err);
      alert('Failed to import file. Please ensure it matches the sample format. Error: ' + (err.message || 'Unknown error'));
    } finally {
      e.target.value = '';
    }
  };

  const handleImportConfirm = async () => {
    if (importValidCustomers.length === 0) return;

    try {
      setLoading(true);
      setError('');
      
      // Fetch states and districts for lookup
      let statesList = [];
      let districtsByState = {}; // Map of state_id -> districts array
      
      try {
        const statesResponse = await getStates();
        if (Array.isArray(statesResponse)) {
          statesList = statesResponse;
        } else if (Array.isArray(statesResponse?.data)) {
          statesList = statesResponse.data;
        } else if (Array.isArray(statesResponse?.data?.data)) {
          statesList = statesResponse.data.data;
        } else if (statesResponse?.success && Array.isArray(statesResponse?.data)) {
          statesList = statesResponse.data;
        }
        
        // Fetch districts for all states
        for (const state of statesList) {
          const stateId = state.id || state.state_id;
          if (stateId) {
            try {
              const districtsResponse = await getDistrictsByState(stateId);
              const districts = Array.isArray(districtsResponse) 
                ? districtsResponse 
                : (districtsResponse?.data || districtsResponse?.items || []);
              districtsByState[stateId] = districts;
            } catch (err) {
              console.warn(`Failed to fetch districts for state ${state.name || state.state_name}:`, err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching states/districts for import:', err);
      }
      
      let successCount = 0;
      let failedCount = 0;
      const failedCustomers = [];

      // Process each customer
      for (const customer of importValidCustomers) {
        try {
          // Resolve state name to state_id
          let stateId = null;
          let stateName = (customer.state || '').trim();
          if (stateName) {
            const matchedState = statesList.find(s => {
              const sName = (s.name || s.state_name || s.state || '').trim();
              return sName.toLowerCase() === stateName.toLowerCase();
            });
            if (matchedState) {
              stateId = matchedState.id || matchedState.state_id;
              stateName = matchedState.name || matchedState.state_name || matchedState.state || stateName;
            }
          }
          
          // Resolve city/district name to city_id (district_id)
          let cityId = null;
          let cityName = (customer.city || '').trim();
          if (cityName && stateId && districtsByState[stateId]) {
            const matchedDistrict = districtsByState[stateId].find(d => {
              const dName = (d.name || d.district_name || d.district || '').trim();
              return dName.toLowerCase() === cityName.toLowerCase();
            });
            if (matchedDistrict) {
              cityId = matchedDistrict.id || matchedDistrict.district_id;
              cityName = matchedDistrict.name || matchedDistrict.district_name || matchedDistrict.district || cityName;
            }
          }
          
          console.log(`Agency Import - Customer: ${customer.customerName}, State: "${stateName}" -> ID: ${stateId}, City: "${cityName}" -> ID: ${cityId}`);
          
          // Prepare customer data for API with resolved IDs
          const customerData = mapCustomerToAPI({
            customerId: customer.customerCode || customer.id,
            customerName: customer.customerName,
            panNumber: customer.panNumber || null,
            aadharNumber: customer.aadharNumber || null,
            addressLine1: customer.addressLine1 || null,
            addressLine2: customer.addressLine2 || null,
            city: cityName || null,
            cityId: cityId,
            pincode: customer.pincode || null,
            state: stateName || null,
            stateId: stateId,
            country: customer.country || 'India',
            emailId: customer.emailId || null,
            mobileNumber: customer.mobileNumber,
            alternateContactNumber: customer.alternateContactNumber || null,
            password: undefined, // Password not required for import
            agencyId: currentAgencyId
          });

          // Create customer via API
          await createCustomer(customerData);
          successCount++;
        } catch (err) {
          console.error(`Failed to create customer ${customer.customerName}:`, err);
          failedCount++;
          failedCustomers.push({
            customerName: customer.customerName,
            error: err.message || 'Unknown error'
          });
        }
      }

      // Update state
      setImportSuccessCount(successCount);
      setImportSkippedCount(importInvalidRows.length + failedCount);
      setShowImportModal(false);
      setShowImportSuccess(true);
      setImportValidCustomers([]);
      setImportInvalidRows([]);
      setImportFileName('');

      // Refresh customer list
      await fetchCustomers();

      // Show error message if some customers failed
      if (failedCount > 0) {
        const errorMsg = `${failedCount} customer(s) failed to import:\n${failedCustomers.map(c => `- ${c.customerName}: ${c.error}`).join('\n')}`;
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Error during import:', err);
      setError(err.message || 'Failed to import customers. Please try again.');
      alert(`Import failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportValidCustomers([]);
    setImportInvalidRows([]);
    setImportFileName('');
  };

  // Download sample Excel template with formatting
  const handleDownloadSampleCSV = async () => {
    // Define required fields (will have asterisk)
    const requiredFields = ['Customer Name', 'Mobile Number'];
    
    const headers = [
      'Customer Name',
      'PAN Number',
      'Aadhar Number',
      'Address Line 1',
      'Address Line 2',
      'State',
      'City',
      'Pincode',
      'Country',
      'Email ID',
      'Mobile Number',
      'Alternate Contact Number'
    ];

    // Add asterisk to required fields
    const headersWithAsterisks = headers.map(header => 
      requiredFields.includes(header) ? `${header} *` : header
    );

    // Create workbook and worksheet using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Customer Import Template');

    // Add header row
    worksheet.addRow(headersWithAsterisks);

    // Fetch states and districts from API for dropdowns
    let statesList = [];
    let allDistricts = [];
    const districtsByState = {}; // Map of state_id -> districts array (full objects, not just names)
    
    try {
      const statesResponse = await getStates();
      console.log('Agency Excel - States API response:', statesResponse);
      
      // Try multiple response formats
      if (Array.isArray(statesResponse)) {
        statesList = statesResponse;
      } else if (Array.isArray(statesResponse?.data)) {
        statesList = statesResponse.data;
      } else if (Array.isArray(statesResponse?.items)) {
        statesList = statesResponse.items;
      } else if (Array.isArray(statesResponse?.data?.data)) {
        statesList = statesResponse.data.data;
      } else if (Array.isArray(statesResponse?.data?.items)) {
        statesList = statesResponse.data.items;
      } else if (statesResponse?.success && Array.isArray(statesResponse?.data)) {
        statesList = statesResponse.data;
      }
      
      console.log('Agency Excel - Parsed states list:', statesList);
      
      // Fetch districts for all states (for City dropdown and dependent dropdowns)
      // Store districts by state for dependent dropdown creation
      if (statesList.length > 0) {
        const districtPromises = statesList.map(async (state) => {
          try {
            const stateId = state.id || state.state_id;
            if (!stateId) return { stateId: null, districts: [] };
            const districtsResponse = await getDistrictsByState(stateId);
            const districts = Array.isArray(districtsResponse) 
              ? districtsResponse 
              : (districtsResponse?.data || districtsResponse?.items || []);
            districtsByState[stateId] = districts; // Store full district objects
            return { stateId, districts: districts.map(d => d.name || d.district_name || d.district || '').filter(Boolean) };
          } catch (err) {
            console.warn(`Failed to fetch districts for state ${state.name || state.state_name}:`, err);
            return { stateId: null, districts: [] };
          }
        });
        
        const districtsResults = await Promise.all(districtPromises);
        allDistricts = [...new Set(districtsResults.flatMap(r => r.districts))].sort(); // Get unique districts
      }
    } catch (err) {
      console.error('Error fetching states/districts for Excel:', err);
      // Continue with empty lists - dropdowns won't work but file will still be generated
    }

    // Add sample data row
    const sampleState = statesList.length > 0 ? (statesList[0].name || statesList[0].state_name || 'Tamil Nadu') : 'Tamil Nadu';
    const sampleCity = allDistricts.length > 0 ? allDistricts[0] : 'Chennai';
    
    const sampleRow = [
      'John Doe',                    // Customer Name *
      'ABCDE1234F',                  // PAN Number
      '1234 5678 9012',              // Aadhar Number
      '123 Main Street',             // Address Line 1
      'Near City Mall',              // Address Line 2
      sampleState,                   // State
      sampleCity,                    // City
      '600001',                      // Pincode
      'India',                       // Country
      'john.doe@example.com',        // Email ID
      '9876543210',                  // Mobile Number *
      '9876543211'                   // Alternate Contact Number
    ];
    worksheet.addRow(sampleRow);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Style sample row (row 2) - make it light gray to indicate it's an example
    const sampleDataRow = worksheet.getRow(2);
    sampleDataRow.font = { italic: true, color: { argb: 'FF666666' } };
    sampleDataRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    };

    // Set column widths for better visibility
    worksheet.getColumn(1).width = 20; // Customer Name *
    worksheet.getColumn(2).width = 15; // PAN Number
    worksheet.getColumn(3).width = 18; // Aadhar Number
    worksheet.getColumn(4).width = 25; // Address Line 1
    worksheet.getColumn(5).width = 25; // Address Line 2
    worksheet.getColumn(6).width = 18; // State
    worksheet.getColumn(7).width = 18; // City
    worksheet.getColumn(8).width = 12; // Pincode
    worksheet.getColumn(9).width = 15; // Country
    worksheet.getColumn(10).width = 28; // Email ID
    worksheet.getColumn(11).width = 18; // Mobile Number *
    worksheet.getColumn(12).width = 25; // Alternate Contact Number

    // Add data validation (dropdowns) starting from row 2
    // State dropdown (Column F, index 6) - Use named range to bypass 255 character limit
    if (statesList.length > 0) {
      const stateOptions = statesList.map(s => {
        const name = s.name || s.state_name || s.state || '';
        return name;
      }).filter(Boolean);
      
      console.log('Agency Excel - State options:', stateOptions.length, 'states');
      
      if (stateOptions.length > 0) {
        // Create a helper sheet for states data
        const statesSheet = workbook.addWorksheet('_StatesData', { state: 'hidden' });
        
        // Write states to helper sheet (column A)
        stateOptions.forEach((state, idx) => {
          statesSheet.getCell(idx + 1, 1).value = state;
        });
        
        // Create named range for all states
        const statesRangeName = 'StatesList';
        workbook.definedNames.add(statesRangeName, `'_StatesData'!$A$1:$A$${stateOptions.length}`);
        
        console.log('Agency Excel - Created named range for states:', statesRangeName);
        
        // Use named range in data validation (bypasses 255 char limit)
        // ExcelJS requires formula1 with = prefix for named ranges
        for (let row = 2; row <= 1000; row++) {
          worksheet.getCell(row, 6).dataValidation = {
            type: 'list',
            allowBlank: true,
            formula1: `=${statesRangeName}`, // Use named range with = prefix
            showInputMessage: true,
            promptTitle: 'State',
            prompt: 'Please select a state from the dropdown. Type to search/filter in Excel.'
          };
        }
      } else {
        console.warn('Agency Excel - No valid state options found after filtering');
      }
    } else {
      console.warn('Agency Excel - States list is empty, skipping dropdown');
    }

    // City dropdown (Column G, index 7) - Create dependent dropdowns using named ranges and INDIRECT
    // Strategy: Create named ranges for each state's districts, then use INDIRECT to reference them
    try {
      // Create a helper sheet for district data (hidden) - reuse or create new
      let districtsSheet;
      try {
        districtsSheet = workbook.getWorksheet('_DistrictsData');
      } catch {
        districtsSheet = workbook.addWorksheet('_DistrictsData', { state: 'hidden' });
      }
      
      // Create named ranges for each state's districts
      const stateDistrictRanges = {};
      let currentRow = 1;
      
      for (const state of statesList) { // Process all states
        const stateId = state.id || state.state_id;
        if (!stateId || !districtsByState[stateId]) continue;
        
        const stateName = (state.name || state.state_name || state.state || '').trim();
        if (!stateName) continue;
        
        const districts = districtsByState[stateId] || [];
        if (districts.length === 0) continue;
        
        // Write state name in column A for reference
        districtsSheet.getCell(currentRow, 1).value = stateName;
        
        // Write districts to helper sheet (column B)
        districts.forEach((district, idx) => {
          const districtName = district.name || district.district_name || district.district || '';
          if (districtName) {
            districtsSheet.getCell(currentRow + idx, 2).value = districtName;
          }
        });
        
        // Create named range for this state's districts
        // Sanitize state name for use in named range (Excel named range rules)
        const sanitizedStateName = stateName.replace(/[^A-Za-z0-9_]/g, '_').replace(/_+/g, '_');
        const rangeName = `Districts_${sanitizedStateName}`;
        const startRow = currentRow;
        const endRow = currentRow + districts.length - 1;
        
        workbook.definedNames.add(rangeName, `'_DistrictsData'!$B$${startRow}:$B$${endRow}`);
        
        stateDistrictRanges[stateName.toLowerCase()] = rangeName;
        console.log(`Agency Excel - Created named range "${rangeName}" for state "${stateName}" with ${districts.length} districts`);
        
        currentRow += Math.max(districts.length, 1) + 1; // Add spacing between states
      }
      
      // Apply dependent dropdown using INDIRECT formula
      // Formula: INDIRECT("Districts_" & SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(F2, " ", "_"), "-", "_"), "&", "_"))
      for (let row = 2; row <= 1000; row++) {
        // Create INDIRECT formula that references the state in column F
        // The formula will dynamically look up the named range based on the state name
        const indirectFormula = `INDIRECT("Districts_" & SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(F${row}, " ", "_"), "-", "_"), "&", "_"), ".", "_"))`;
        
        try {
          worksheet.getCell(row, 7).dataValidation = {
            type: 'list',
            allowBlank: true,
            formula1: `=${indirectFormula}`, // Use formula1 with = prefix
            showInputMessage: true,
            promptTitle: 'City (District)',
            prompt: `Please select a city/district from the dropdown. The list depends on the state selected in column F. Type to search/filter in Excel.`
          };
        } catch (err) {
          // If INDIRECT doesn't work, use all districts as fallback
          console.warn(`Agency Row ${row}: Could not use INDIRECT, using all districts:`, err);
          // Create named range for all districts as fallback
          if (allDistricts.length > 0) {
            const allDistrictsSheet = workbook.getWorksheet('_DistrictsData') || workbook.addWorksheet('_DistrictsData', { state: 'hidden' });
            allDistricts.forEach((district, idx) => {
              allDistrictsSheet.getCell(idx + 1, 3).value = district; // Use column C for all districts
            });
            
            const allDistrictsRangeName = 'AllDistricts';
            try {
              workbook.definedNames.add(allDistrictsRangeName, `'_DistrictsData'!$C$1:$C$${allDistricts.length}`);
              
              worksheet.getCell(row, 7).dataValidation = {
                type: 'list',
                allowBlank: true,
                formula1: `=${allDistrictsRangeName}`, // Use formula1 with = prefix
                showInputMessage: true,
                promptTitle: 'City (District)',
                prompt: 'Please select a city/district from the dropdown. Note: Select the district that matches your selected state.'
              };
            } catch (fallbackErr) {
              console.warn('Could not create fallback named range:', fallbackErr);
            }
          }
        }
      }
      
      console.log('Agency Excel - Created dependent dropdowns using named ranges and INDIRECT');
    } catch (err) {
      console.warn('Agency Excel - Could not create dependent dropdowns, using static list:', err);
      // Fallback to static list using named range
      if (allDistricts.length > 0) {
        try {
          const allDistrictsSheet = workbook.addWorksheet('_AllDistrictsData', { state: 'hidden' });
          allDistricts.forEach((district, idx) => {
            allDistrictsSheet.getCell(idx + 1, 1).value = district;
          });
          
          const allDistrictsRangeName = 'AllDistrictsList';
          workbook.definedNames.add(allDistrictsRangeName, `'_AllDistrictsData'!$A$1:$A$${allDistricts.length}`);
          
          for (let row = 2; row <= 1000; row++) {
            worksheet.getCell(row, 7).dataValidation = {
              type: 'list',
              allowBlank: true,
              formula1: `=${allDistrictsRangeName}`, // Use formula1 with = prefix
              showInputMessage: true,
              promptTitle: 'City (District)',
              prompt: 'Please select a city/district from the dropdown. Note: Select the district that matches your selected state (optional)'
            };
          }
        } catch (fallbackErr) {
          console.error('Could not create fallback named range:', fallbackErr);
        }
      }
    }

    // Country dropdown (Column I, index 9) - only India
    const countryOptions = ['India'];
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(row, 9).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${countryOptions.join(',')}"`],
        showInputMessage: true,
        promptTitle: 'Country',
        prompt: 'Please select a country from the dropdown (optional)'
      };
    }

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Customer_Import_Sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = (e) => {
    e.preventDefault();
    setShowSampleCSVModal(true);
  };

  const handleProceedWithImport = () => {
    setShowSampleCSVModal(false);
    document.getElementById('customer-import-file-input').click();
  };

  // Generate auto Customer ID from database
  const generateCustomerId = async () => {
    try {
      const nextId = await getNextCustomerId();
      return nextId || 'CUST-001'; // Fallback if API fails
    } catch (error) {
      console.error('Error generating customer ID:', error);
      // Fallback: calculate from loaded customers (not ideal, but better than nothing)
      const existingIds = customers.map(c => {
        const match = (c.customerCode || '').match(/CUST-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      });
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
      return `CUST-${String(nextId).padStart(3, '0')}`;
    }
  };

  const handleAddCustomer = async () => {
    try {
      console.log('Generating customer ID...');
      const newId = await generateCustomerId();
      console.log('Generated customer ID:', newId);
      setFormData({
        ...formData,
        customerId: newId
      });
      setShowAddModal(true);
    } catch (error) {
      console.error('Error in handleAddCustomer:', error);
      // Still open modal with fallback ID
      setFormData({
        ...formData,
        customerId: 'CUST-001'
      });
      setShowAddModal(true);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({...formData, photo: file, photoPreview: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingCustomer(null);
    setFormData({
      customerId: '',
      customerName: '',
      panNumber: '',
      aadharNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      cityId: null,
      pincode: '',
      state: '',
      stateId: null,
      country: 'India',
      emailId: '',
      mobileNumber: '',
      alternateContactNumber: '',
      photo: null,
      photoPreview: null
    });
    setFormErrors({});
  };

  const handleReset = async () => {
    if (showEditModal && editingCustomer) {
      // Reset to original customer data (will be handled by handleEditClick logic)
      await handleEditClick(editingCustomer);
    } else {
      // Reset to empty form with new ID from database
      const newId = await generateCustomerId();
      setFormData({
        customerId: newId,
        customerName: '',
        panNumber: '',
        aadharNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        cityId: null,
        pincode: '',
        state: '',
        stateId: null,
        country: 'India',
        emailId: '',
        mobileNumber: '',
        alternateContactNumber: '',
        photo: null,
        photoPreview: null
      });
    }
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.customerName.trim()) {
      errors.customerName = 'Customer Name is required';
    }
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile Number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      errors.mobileNumber = 'Mobile Number must be 10 digits';
    }
    if (formData.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      errors.emailId = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleViewClick = (customer) => {
    setViewingCustomer(customer);
    setShowViewModal(true);
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (customerToDelete) {
      try {
        // Delete customer via API
        await api.delete(`/api/customers/${customerToDelete.id}`);
        
        setDeletedCustomerInfo({
          customerName: customerToDelete.customerName
        });
        setShowDeleteConfirm(false);
        setCustomerToDelete(null);
        setShowDeleteSuccess(true);

        // Refresh customers list
        await fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert(error.message || 'Failed to delete customer. Please try again.');
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setCustomerToDelete(null);
  };

  const handleDeleteSuccessClose = () => {
    setShowDeleteSuccess(false);
    setDeletedCustomerInfo(null);
  };

  const handleEditClick = async (customer) => {
    setEditingCustomer(customer);
    
    // Use IDs from API response if available, otherwise look them up
    let stateId = customer.stateId || null;
    let cityId = customer.cityId || null;
    
    // If stateId is available, load districts for the city dropdown
    if (stateId) {
      try {
        const districtsResponse = await getDistrictsByState(stateId);
        let districtsData = [];
        if (Array.isArray(districtsResponse)) {
          districtsData = districtsResponse;
        } else if (Array.isArray(districtsResponse?.data)) {
          districtsData = districtsResponse.data;
        } else if (Array.isArray(districtsResponse?.items)) {
          districtsData = districtsResponse.items;
        }
        setDistricts(districtsData);
      } catch (err) {
        console.error('Agency - Error loading districts for edit:', err);
        setDistricts([]);
      }
    } else if (customer.state) {
      // Fallback: Find state ID from state name if not provided by API
      const selectedState = states.find(s => {
        const stateName = (s.name || s.state_name || s.state || '').toLowerCase().trim();
        return stateName === customer.state.toLowerCase().trim();
      });
      stateId = selectedState ? (selectedState.id || selectedState.state_id) : null;
      
      // If state found, load districts to find city ID
      if (stateId) {
        try {
          const districtsResponse = await getDistrictsByState(stateId);
          let districtsData = [];
          if (Array.isArray(districtsResponse)) {
            districtsData = districtsResponse;
          } else if (Array.isArray(districtsResponse?.data)) {
            districtsData = districtsResponse.data;
          } else if (Array.isArray(districtsResponse?.items)) {
            districtsData = districtsResponse.items;
          }
          setDistricts(districtsData);
          
          // Find district ID from district name if not provided by API
          if (!cityId && customer.city) {
            const selectedDistrict = districtsData.find(d => {
              const districtName = (d.name || d.district_name || d.district || '').toLowerCase().trim();
              return districtName === customer.city.toLowerCase().trim();
            });
            cityId = selectedDistrict ? (selectedDistrict.id || selectedDistrict.district_id) : null;
          }
        } catch (err) {
          console.error('Agency - Error loading districts for edit:', err);
          setDistricts([]);
        }
      }
    }
    
    setFormData({
      customerId: customer.customerCode || customer.id,
      customerName: customer.customerName || '',
      panNumber: customer.panNumber || '',
      aadharNumber: customer.aadharNumber || '',
      addressLine1: customer.addressLine1 || '',
      addressLine2: customer.addressLine2 || '',
      city: customer.city || '',
      cityId: cityId,
      pincode: customer.pincode || '',
      state: customer.state || '',
      stateId: stateId,
      country: customer.country || 'India',
      emailId: customer.emailId || '',
      mobileNumber: customer.mobileNumber || '',
      alternateContactNumber: customer.alternateContactNumber || '',
      photo: null,
      photoPreview: customer.photo || null
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleSaveCustomer = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      if (editingCustomer) {
        // Update existing customer via API
        console.log('Agency - Form data before mapping (update):', {
          state: formData.state,
          stateId: formData.stateId,
          city: formData.city,
          cityId: formData.cityId
        });
        
        const customerPayload = mapCustomerToAPI({
          customerId: formData.customerId,
          customerName: formData.customerName,
          panNumber: formData.panNumber,
          aadharNumber: formData.aadharNumber,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          cityId: formData.cityId,
          pincode: formData.pincode,
          state: formData.state,
          stateId: formData.stateId,
          country: formData.country,
          emailId: formData.emailId,
          password: formData.password,
          mobileNumber: formData.mobileNumber,
          alternateContactNumber: formData.alternateContactNumber,
          photo: formData.photo,
          agencyId: currentAgencyId
        });

        console.log('Agency - Update customer payload:', customerPayload);
        // Use PUT request to update customer
        await api.put(`/api/customers/${editingCustomer.id}`, customerPayload);

        setSuccessCustomerName(formData.customerName);
        setSuccessModalType('update');
        handleCloseModal();
        setShowSuccessModal(true);

        await fetchCustomers();
      } else {
        // Create new customer via API
        console.log('Agency - Form data before mapping:', {
          state: formData.state,
          stateId: formData.stateId,
          city: formData.city,
          cityId: formData.cityId
        });
        
        const customerPayload = mapCustomerToAPI({
          customerId: formData.customerId,
          customerName: formData.customerName,
          panNumber: formData.panNumber,
          aadharNumber: formData.aadharNumber,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          cityId: formData.cityId,
          pincode: formData.pincode,
          state: formData.state,
          stateId: formData.stateId,
          country: formData.country,
          emailId: formData.emailId,
          password: formData.password,
          mobileNumber: formData.mobileNumber,
          alternateContactNumber: formData.alternateContactNumber,
          photo: formData.photo,
          agencyId: currentAgencyId
        });

        console.log('Agency - Create customer payload:', customerPayload);
        await createCustomer(customerPayload);

        setSuccessCustomerName(formData.customerName);
        setSuccessModalType('add');
        handleCloseModal();
        setShowSuccessModal(true);

        await fetchCustomers();
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = error.message || 'Failed to save customer. Please try again.';
      setFormErrors(prev => ({ ...prev, general: errorMessage }));
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Indian states and cities mapping
  const indianStatesAndCities = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole', 'Nandyal', 'Machilipatnam', 'Adoni', 'Tenali', 'Chittoor', 'Hindupur', 'Proddatur', 'Bhimavaram'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Tawang', 'Bomdila', 'Pasighat', 'Tezu', 'Ziro', 'Along', 'Daporijo', 'Namsai', 'Roing', 'Khonsa', 'Aalo', 'Seppa', 'Yingkiong'],
    'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon', 'Dhubri', 'Diphu', 'North Lakhimpur', 'Karimganj', 'Goalpara', 'Barpeta', 'Sivasagar', 'Hailakandi', 'Golaghat', 'Dhekiajuli', 'Mangaldoi'],
    'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Bihar Sharif', 'Katihar', 'Munger', 'Chapra', 'Sasaram', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Bettiah', 'Motihari', 'Saharsa', 'Buxar', 'Kishanganj'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajpur', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Chirmiri', 'Dhamtari', 'Dantewada', 'Kanker', 'Kawardha', 'Mahasamund', 'Surajpur'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim', 'Valpoi', 'Curchorem', 'Quepem', 'Canacona'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Gandhidham', 'Anand', 'Navsari', 'Morbi', 'Nadiad', 'Surendranagar', 'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Valsad'],
    'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula', 'Sirsa', 'Bhiwani', 'Bahadurgarh', 'Jind', 'Thanesar', 'Kaithal', 'Rewari', 'Palwal', 'Fatehabad', 'Hansi'],
    'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Hamirpur', 'Una', 'Kangra', 'Nahan', 'Palampur', 'Sundarnagar', 'Nurpur', 'Keylong'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro Steel City', 'Hazaribagh', 'Deoghar', 'Giridih', 'Adityapur', 'Ramgarh', 'Medininagar', 'Chirkunda', 'Phusro', 'Chaibasa', 'Gumla', 'Lohardaga', 'Dumka', 'Pakur', 'Godda'],
    'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belagavi', 'Gulbarga', 'Davangere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Udupi', 'Gadag', 'Chitradurga', 'Kolar', 'Mandya'],
    'Kerala': ['Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Kottayam', 'Palakkad', 'Manjeri', 'Thalassery', 'Ponnani', 'Kochi', 'Kayamkulam', 'Kottarakkara', 'Changanassery', 'Kasaragod', 'Pathanamthitta', 'Idukki', 'Wayanad'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Ratlam', 'Satna', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Morena', 'Bhind', 'Chhindwara', 'Vidisha', 'Guna', 'Shivpuri', 'Damoh'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kalyan', 'Vasai-Virar', 'Navi Mumbai', 'Amravati', 'Nanded', 'Kolhapur', 'Sangli', 'Jalgaon', 'Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Ichalkaranji', 'Jalna', 'Bhusawal', 'Panvel', 'Satara', 'Beed', 'Yavatmal', 'Kamptee', 'Gondia', 'Barshi', 'Achalpur'],
    'Manipur': ['Imphal', 'Thoubal', 'Kakching', 'Ukhrul', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Jiribam', 'Moreh', 'Lilong', 'Moirang', 'Mayang Imphal'],
    'Meghalaya': ['Shillong', 'Tura', 'Nongpoh', 'Jowai', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara', 'Mankachar', 'Ampati'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit', 'Khawzawl', 'Hnahthial'],
    'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Kiphire', 'Longleng', 'Peren'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Baleshwar', 'Bhadrak', 'Baripada', 'Balangir', 'Jharsuguda', 'Bargarh', 'Rayagada', 'Jeypore', 'Dhenkanal', 'Barbil', 'Kendujhar', 'Paradip', 'Bhuban', 'Talcher'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga', 'Abohar', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Firozpur', 'Kapurthala', 'Sultanpur Lodhi', 'Fazilka', 'Gurdaspur', 'Malerkotla'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar', 'Bharatpur', 'Sri Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Chittorgarh', 'Hanumangarh', 'Beawar', 'Karauli', 'Dholpur', 'Bundi', 'Baran', 'Churu', 'Jhunjhunu', 'Nagaur', 'Jhalawar', 'Banswara'],
    'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Pakyong', 'Rhenock', 'Soreng'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Karaikudi', 'Neyveli', 'Cuddalore', 'Kumbakonam', 'Tiruvannamalai', 'Pollachi', 'Rajapalayam', 'Gudiyatham', 'Pudukkottai', 'Ambur'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Jagtial', 'Mancherial', 'Kamareddy', 'Kothagudem', 'Bhongir', 'Bodhan', 'Narayanpet', 'Wanaparthy'],
    'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar', 'Belonia', 'Khowai', 'Ambassa', 'Kumarghat', 'Sabroom', 'Teliamura'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Meerut', 'Varanasi', 'Allahabad', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Noida', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Rampur', 'Shahjahanpur', 'Farrukhabad', 'Fatehpur', 'Budaun', 'Sitapur', 'Hapur', 'Etawah', 'Unnao', 'Pilibhit', 'Lakhimpur', 'Basti', 'Deoria'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital', 'Mussoorie', 'Pithoragarh', 'Almora', 'Srinagar', 'Kotdwara', 'Ramnagar', 'Pauri', 'Chamoli Gopeshwar', 'New Tehri', 'Uttarkashi'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Ranaghat', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Chandannagar', 'Jalpaiguri', 'Baidyabati', 'Darjeeling', 'Purulia', 'Bankura', 'Alipurduar', 'Raiganj'],
    'Delhi': ['New Delhi', 'Delhi Cantonment', 'Narela', 'Najafgarh', 'Rohini', 'Dwarka', 'Patparganj'],
    'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Poonch', 'Rajouri', 'Kupwara', 'Bandipora', 'Ganderbal', 'Pulwama', 'Shopian', 'Budgam', 'Kulgam', 'Doda', 'Ramban', 'Kishtwar', 'Reasi'],
    'Ladakh': ['Leh', 'Kargil', 'Drass', 'Nubra Valley', 'Zanskar'],
    'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
    'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar'],
    'Chandigarh': ['Chandigarh'],
    'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
    'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat', 'Kalpeni', 'Kiltan', 'Minicoy']
  };

  // Get districts (cities) for selected state - now from API
  const getCitiesForState = (stateName) => {
    if (!stateName || districts.length === 0) {
      console.log('Agency getCitiesForState: No districts available. stateName:', stateName, 'districts.length:', districts.length);
      return [];
    }
    // Districts are already filtered by state from the API call
    const districtNames = districts
      .map(d => d.name || d.district_name || d.district || '')
      .filter(Boolean)
      .sort();
    console.log('Agency getCitiesForState: Returning', districtNames.length, 'districts for state', stateName);
    return districtNames;
  };

  // Filtered states for form dropdown
  const filteredFormStates = useMemo(() => {
    if (!formStateSearch) return states;
    const searchLower = formStateSearch.toLowerCase();
    return states.filter(s => {
      const stateName = (s.name || s.state_name || s.state || '').toLowerCase();
      return stateName.includes(searchLower);
    });
  }, [states, formStateSearch]);

  // Filtered cities for form dropdown
  const filteredFormCities = useMemo(() => {
    const cities = getCitiesForState(formData.state);
    if (!formCitySearch) return cities;
    const searchLower = formCitySearch.toLowerCase();
    return cities.filter(city => city.toLowerCase().includes(searchLower));
  }, [formData.state, districts, formCitySearch]);

  // Load districts for filter when state filter changes
  useEffect(() => {
    if (!stateFilter || !states.length) {
      setFilterDistricts([]);
      return;
    }

    const loadFilterDistricts = async () => {
      // Find the selected state from states array
      const selectedState = states.find(s => {
        const stateName = (s.name || s.state_name || s.state || '').trim();
        return stateName === stateFilter;
      });

      if (!selectedState) {
        setFilterDistricts([]);
        return;
      }

      const stateId = selectedState.id || selectedState.state_id;
      if (!stateId) {
        setFilterDistricts([]);
        return;
      }

      try {
        setLoadingFilterDistricts(true);
        const response = await getDistrictsByState(stateId);
        console.log('Agency Filter - Districts API response for state', stateFilter, ':', response);

        // Try multiple response formats
        let districtsData = [];
        if (Array.isArray(response)) {
          districtsData = response;
        } else if (Array.isArray(response?.data)) {
          districtsData = response.data;
        } else if (Array.isArray(response?.items)) {
          districtsData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          districtsData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          districtsData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          districtsData = response.data;
        }

        setFilterDistricts(districtsData);
        console.log('Agency Filter - Loaded', districtsData.length, 'districts for state', stateFilter);
      } catch (err) {
        console.error('Agency Filter - Error loading districts for state', stateFilter, ':', err);
        setFilterDistricts([]);
      } finally {
        setLoadingFilterDistricts(false);
      }
    };

    loadFilterDistricts();
  }, [stateFilter, states]);

  return (
    <div className="customer-management-container">
      <div className="customer-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={customerNameDropdownRef}>
            <button
              className={`filter-btn ${customerNameFilter ? 'active' : ''}`}
              onClick={() => {
                setCustomerNameDropdownOpen(!customerNameDropdownOpen);
                setCityDropdownOpen(false);
                setStateDropdownOpen(false);
                if (!customerNameDropdownOpen) {
                  setCustomerNameSearch('');
                }
              }}
            >
              <span>👤</span> Customer Name {customerNameFilter && `(${customerNameFilter.split(' ')[0]}...)`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {customerNameDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder="Search customer name..."
                    value={customerNameSearch}
                    onChange={(e) => setCustomerNameSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <button className={`filter-option ${customerNameFilter === '' ? 'selected' : ''}`} onClick={() => { setCustomerNameFilter(''); setCustomerNameDropdownOpen(false); setCustomerNameSearch(''); clearPaging(); }}>All</button>
                {filteredCustomerNames.map((name) => (
                  <button key={name} className={`filter-option ${customerNameFilter === name ? 'selected' : ''}`} onClick={() => { setCustomerNameFilter(name); setCustomerNameDropdownOpen(false); setCustomerNameSearch(''); clearPaging(); }}>{name}</button>
                ))}
                {filteredCustomerNames.length === 0 && (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>No results found</div>
                )}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={stateDropdownRef}>
            <button
              className={`filter-btn ${stateFilter ? 'active' : ''}`}
              onClick={() => {
                setStateDropdownOpen(!stateDropdownOpen);
                setCustomerNameDropdownOpen(false);
                setCityDropdownOpen(false);
                if (!stateDropdownOpen) {
                  setStateSearch('');
                }
              }}
            >
              <span>📍</span> State {stateFilter && `(${stateFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {stateDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder="Search state..."
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <button className={`filter-option ${stateFilter === '' ? 'selected' : ''}`} onClick={() => { 
                  setStateFilter(''); 
                  setCityFilter(''); // Clear city when state is cleared
                  setStateDropdownOpen(false); 
                  setStateSearch(''); 
                  clearPaging(); 
                }}>All</button>
                {loadingStates ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Loading states...</div>
                ) : filteredFilterStates.length === 0 ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                    {stateSearch ? 'No states found' : 'No states available'}
                  </div>
                ) : (
                  filteredFilterStates.map((state) => {
                    const stateName = state.name || state.state_name || state.state || '';
                    if (!stateName) return null;
                    return (
                      <button 
                        key={state.id || state.state_id || stateName} 
                        className={`filter-option ${stateFilter === stateName ? 'selected' : ''}`} 
                        onClick={() => { 
                          setStateFilter(stateName); 
                          setCityFilter(''); // Clear city when state changes
                          setStateDropdownOpen(false); 
                          setStateSearch(''); 
                          clearPaging(); 
                        }}
                      >
                        {stateName}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={cityDropdownRef}>
            <button
              className={`filter-btn ${cityFilter ? 'active' : ''}`}
              onClick={() => {
                setCityDropdownOpen(!cityDropdownOpen);
                setCustomerNameDropdownOpen(false);
                setStateDropdownOpen(false);
                if (!cityDropdownOpen) {
                  setCitySearch('');
                }
              }}
              disabled={!stateFilter} // Disable if no state selected
            >
              <span>📍</span> City {cityFilter && `(${cityFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {cityDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder={stateFilter ? "Search city/district..." : "Select state first"}
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!stateFilter}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      opacity: stateFilter ? 1 : 0.6
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                {!stateFilter ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Please select a state first</div>
                ) : (
                  <>
                    <button className={`filter-option ${cityFilter === '' ? 'selected' : ''}`} onClick={() => { setCityFilter(''); setCityDropdownOpen(false); setCitySearch(''); clearPaging(); }}>All</button>
                    {loadingFilterDistricts ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Loading districts...</div>
                    ) : filteredFilterCities.length === 0 ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                        {citySearch ? 'No districts found' : 'No districts available'}
                      </div>
                    ) : (
                      filteredFilterCities.map((district) => {
                        const districtName = district.name || district.district_name || district.district || '';
                        if (!districtName) return null;
                        return (
                          <button 
                            key={district.id || district.district_id || districtName} 
                            className={`filter-option ${cityFilter === districtName ? 'selected' : ''}`} 
                            onClick={() => { 
                              setCityFilter(districtName); 
                              setCityDropdownOpen(false); 
                              setCitySearch(''); 
                              clearPaging(); 
                            }}
                          >
                            {districtName}
                          </button>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="customer-actions">
          <button className="btn-primary" onClick={handleAddCustomer}><span>+</span> Add Customer</button>
          <label className="btn-icon-only" style={{ cursor: 'pointer', margin: 0 }} onClick={handleImportClick} title="Import">
            <span>📥</span>
            <input
              id="customer-import-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportFileChange}
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn-icon-only" onClick={handleExport} title="Export"><span>📤</span></button>
        </div>
      </div>

      <div className="show-entries-container">
        <div className="show-entries-wrapper">
          <div className="show-entries-left">
            <span className="show-entries-label">Show</span>
        <div className="filter-wrapper" ref={showEntriesDropdownRef}>
          <button
            className="show-entries-btn"
            onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
          >
            {itemsPerPage} <span>▼</span>
          </button>
          {showEntriesDropdownOpen && (
            <div className="filter-dropdown">
              {[10, 25, 50, 100].map(num => (
                <button
                  key={num}
                  className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                  onClick={() => {
                    setItemsPerPage(num);
                    setShowEntriesDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="show-entries-label">entries</span>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, code, mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      <div className="customer-table-container">
        <table className="customer-table">
          <thead>
            <tr>
              <th>Customer Code</th>
              <th>Customer Details</th>
              <th>City, State</th>
              <th>Address Line 1</th>
              <th>Address Line 2</th>
              <th>Aadhar Number</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer.id}>
                <td>{customer.customerCode}</td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontWeight: 600, color: '#333' }}>{customer.customerName || '-'}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      📱 {customer.mobileNumber || '-'}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      ✉️ {customer.emailId || '-'}
                    </div>
                  </div>
                </td>
                <td>
                  {customer.city && customer.state 
                    ? `${customer.city}, ${customer.state}`
                    : customer.city || customer.state || '-'
                  }
                </td>
                <td>{customer.addressLine1 || '-'}</td>
                <td>{customer.addressLine2 || '-'}</td>
                <td>{customer.aadharNumber || '-'}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn view-btn" title="View" onClick={() => handleViewClick(customer)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                      </svg>
                    </button>
                    <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(customer)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 3.333L12.667 4.667M10.667 2L14 5.333L5.333 14H2V10.667L10.667 2Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <button className="action-btn delete-btn" title="Delete" onClick={() => handleDeleteClick(customer)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="5.5" width="8" height="8.5" rx="0.5" stroke="#dc2626" strokeWidth="1.25" fill="none"/>
                        <path d="M2 5.5h12M6 5.5V4.25C6 3.83579 6.33579 3.5 6.75 3.5h2.5C9.66421 3.5 10 3.83579 10 4.25V5.5" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="6.5" y1="8.5" x2="6.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="9.5" y1="8.5" x2="9.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="customer-pagination">
        <div className="pagination-info">
          Showing {customers.length === 0 ? 0 : pageStart + 1} to {Math.min(pageStart + customers.length, totalCount)} of {totalCount} entries
        </div>
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>←</button>
          <button className="pagination-btn active">{currentPage}</button>
          <button className="pagination-btn" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>→</button>
        </div>
      </div>

      {/* Add/Edit Customer Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <div>{showEditModal ? 'Edit Customer' : 'Add Customer'}</div>
              <button className="action-btn" onClick={handleCloseModal}>✖</button>
            </div>
            <div className="modal-body">
              {/* Customer Details Section with Photo on Right */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
                {/* Left Side - First 4 Fields in 2x2 Grid */}
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div className="form-field">
                    <label>
                      Customer Id
                      <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: '#666', fontWeight: 'normal' }}>
                        (Auto-generated)
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px', display: 'inline-block', verticalAlign: 'middle', cursor: 'help' }} title="Auto-generated, can be manually entered">
                          <circle cx="8" cy="8" r="7" stroke="#999" strokeWidth="1.5" fill="none"/>
                          <path d="M8 6V8M8 10H8.01" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </span>
                    </label>
                    <input 
                      className="form-input" 
                      value={formData.customerId} 
                      readOnly
                      placeholder="Auto-generated"
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>
                      Customer Name <span className="required-asterisk">*</span>
                    </label>
                    <input 
                      className={`form-input ${formErrors.customerName ? 'error' : ''}`}
                      placeholder="Enter customer name" 
                      value={formData.customerName}
                      onChange={(e) => {
                        setFormData({ ...formData, customerName: e.target.value });
                        if (formErrors.customerName) setFormErrors({...formErrors, customerName: ''});
                      }}
                    />
                    {formErrors.customerName && <div className="error-message">{formErrors.customerName}</div>}
                  </div>
                  <div className="form-field">
                    <label>PAN Number</label>
                    <input 
                      className="form-input" 
                      placeholder="Enter PAN number" 
                      value={formData.panNumber}
                      onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Aadhar Number</label>
                    <input 
                      className="form-input" 
                      placeholder="Enter Aadhar number" 
                      value={formData.aadharNumber}
                      onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                    />
                  </div>
                </div>
                
                {/* Right Side - Photo Upload (spanning 2 rows) */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: 500, color: '#333', marginBottom: '8px', alignSelf: 'flex-start' }}>Photo</label>
                  <div className="photo-upload-box" style={{ width: '150px', height: '150px' }}>
                    {formData.photoPreview ? (
                      <>
                        <img src={formData.photoPreview} alt="Customer preview" className="photo-preview" />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => {
                            setFormData({...formData, photo: null, photoPreview: null});
                            document.getElementById('customer-photo-input').value = '';
                          }}
                        >
                          ✕ Remove
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="photo-upload-icon">📷</div>
                        <p className="photo-upload-text">Upload Photo</p>
                        <p className="photo-upload-hint">JPG or PNG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="customer-photo-input"
                      accept="image/jpeg,image/png"
                      className="photo-upload-input"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                </div>
              </div>

              {/* Address & Contact Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Address & Contact</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter address line 1" 
                    value={formData.addressLine1}
                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter address line 2" 
                    value={formData.addressLine2}
                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <div className="searchable-select-wrapper" ref={formStateDropdownRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={loadingStates ? 'Loading states...' : 'Search and select state'}
                      value={formStateDropdownOpen ? formStateSearch : (formData.state || '')}
                      onFocus={() => {
                        setFormStateDropdownOpen(true);
                        setFormStateSearch('');
                      }}
                      onChange={(e) => {
                        setFormStateSearch(e.target.value);
                        setFormStateDropdownOpen(true);
                      }}
                      onClick={() => setFormStateDropdownOpen(true)}
                      disabled={loadingStates}
                    />
                    {formStateDropdownOpen && (
                      <div className="searchable-dropdown" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        width: '100%',
                        maxHeight: '200px',
                        overflow: 'auto',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}>
                        {loadingStates ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>Loading states...</div>
                        ) : filteredFormStates.length === 0 ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                            {formStateSearch ? 'No states found' : 'No states available'}
                          </div>
                        ) : (
                          filteredFormStates.map(state => {
                            const stateName = state.name || state.state_name || state.state || '';
                            if (!stateName) return null;
                            return (
                              <div
                                key={state.id || state.state_id || stateName}
                                onClick={() => {
                                  const stateId = state.id || state.state_id;
                                  setFormData({ 
                                    ...formData, 
                                    state: stateName, 
                                    stateId: stateId,
                                    city: '',
                                    cityId: null
                                  });
                                  setDistricts([]);
                                  setFormStateDropdownOpen(false);
                                  setFormStateSearch('');
                                }}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  backgroundColor: formData.state === stateName ? '#f3f4f6' : '#fff'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = formData.state === stateName ? '#f3f4f6' : '#fff'}
                              >
                                {stateName}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>City</label>
                  <div className="searchable-select-wrapper" ref={formCityDropdownRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={!formData.state ? 'Select State first' : (loadingDistricts ? 'Loading districts...' : 'Search and select city')}
                      value={formCityDropdownOpen ? formCitySearch : (formData.city || '')}
                      onFocus={() => {
                        if (formData.state && !loadingDistricts) {
                          setFormCityDropdownOpen(true);
                          setFormCitySearch('');
                        }
                      }}
                      onChange={(e) => {
                        setFormCitySearch(e.target.value);
                        setFormCityDropdownOpen(true);
                      }}
                      onClick={() => {
                        if (formData.state && !loadingDistricts) {
                          setFormCityDropdownOpen(true);
                        }
                      }}
                      disabled={!formData.state || loadingDistricts}
                    />
                    {formCityDropdownOpen && formData.state && (
                      <div className="searchable-dropdown" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        width: '100%',
                        maxHeight: '200px',
                        overflow: 'auto',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}>
                        {loadingDistricts ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>Loading districts...</div>
                        ) : filteredFormCities.length === 0 ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                            {formCitySearch ? 'No cities found' : 'No districts available'}
                          </div>
                        ) : (
                          filteredFormCities.map(city => {
                            // Find district ID from districts array
                            const district = districts.find(d => {
                              const districtName = d.name || d.district_name || d.district || '';
                              return districtName === city;
                            });
                            const districtId = district ? (district.id || district.district_id) : null;
                            
                            return (
                            <div
                              key={city}
                              onClick={() => {
                                setFormData({ ...formData, city, cityId: districtId });
                                setFormCityDropdownOpen(false);
                                setFormCitySearch('');
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6',
                                backgroundColor: formData.city === city ? '#f3f4f6' : '#fff'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = formData.city === city ? '#f3f4f6' : '#fff'}
                            >
                              {city}
                            </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter pincode" 
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Country</label>
                  <select 
                    className="form-input" 
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  >
                    <option value="India">India</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Email ID</label>
                  <div className="input-with-suffix">
                    <input 
                      className={`form-input ${formErrors.emailId ? 'error' : ''}`}
                      type="email"
                      placeholder="Enter email address" 
                      value={formData.emailId}
                      onChange={(e) => {
                        setFormData({ ...formData, emailId: e.target.value });
                        if (formErrors.emailId) setFormErrors({...formErrors, emailId: ''});
                      }}
                    />
                    <span className="input-suffix">✉</span>
                  </div>
                  {formErrors.emailId && <div className="error-message">{formErrors.emailId}</div>}
                </div>
                <div className="form-field">
                  <label>Mobile Number</label>
                  <div className="input-with-suffix">
                    <input 
                      className={`form-input ${formErrors.mobileNumber ? 'error' : ''}`}
                      type="tel"
                      placeholder="Enter mobile number" 
                      value={formData.mobileNumber}
                      onChange={(e) => {
                        setFormData({ ...formData, mobileNumber: e.target.value });
                        if (formErrors.mobileNumber) setFormErrors({...formErrors, mobileNumber: ''});
                      }}
                      maxLength="10"
                    />
                    <span className="input-suffix">📱</span>
                  </div>
                  {formErrors.mobileNumber && <div className="error-message">{formErrors.mobileNumber}</div>}
                </div>
                <div className="form-field">
                  <label>Alternate Contact Number</label>
                  <div className="input-with-suffix">
                    <input 
                      className="form-input"
                      type="tel"
                      placeholder="Enter alternate contact" 
                      value={formData.alternateContactNumber}
                      onChange={(e) => setFormData({ ...formData, alternateContactNumber: e.target.value })}
                      maxLength="10"
                    />
                    <span className="input-suffix">☎</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>Close</button>
              <button className="btn-secondary" onClick={handleReset}>Reset</button>
              <button className="btn-primary" onClick={handleSaveCustomer}>{showEditModal ? 'Update' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View Customer Modal */}
      {showViewModal && viewingCustomer && (
        <div className="modal-overlay" onClick={() => { setShowViewModal(false); setViewingCustomer(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <div>View Customer</div>
              <button className="action-btn" onClick={() => { setShowViewModal(false); setViewingCustomer(null); }}>✖</button>
            </div>
            <div className="modal-body">
              {/* Photo Display Section - At Top */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '16px' }}>Photo</h3>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <div className="photo-upload-box" style={{ cursor: 'default' }}>
                  {viewingCustomer.photo ? (
                    <img src={viewingCustomer.photo} alt="Customer" className="photo-preview" />
                  ) : (
                    <>
                      <div className="photo-upload-icon">📷</div>
                      <p className="photo-upload-text">No Photo</p>
                    </>
                  )}
                </div>
              </div>

              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {/* Customer Details Section */}
                <div className="form-field">
                  <label>Customer Id</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.customerCode || viewingCustomer.id || ''} 
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Customer Name</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.customerName || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>PAN Number</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.panNumber || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Aadhar Number</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.aadharNumber || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              {/* Address & Contact Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Address & Contact</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.addressLine1 || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.addressLine2 || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.city || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.pincode || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.state || ''}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Country</label>
                  <input 
                    className="form-input" 
                    value={viewingCustomer.country || 'India'}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>Email ID</label>
                  <div className="input-with-suffix">
                    <input 
                      className="form-input"
                      type="email"
                      value={viewingCustomer.emailId || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <span className="input-suffix">✉</span>
                  </div>
                </div>
                <div className="form-field">
                  <label>Mobile Number</label>
                  <div className="input-with-suffix">
                    <input 
                      className="form-input"
                      type="tel"
                      value={viewingCustomer.mobileNumber || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <span className="input-suffix">📱</span>
                  </div>
                </div>
                <div className="form-field">
                  <label>Alternate Contact Number</label>
                  <div className="input-with-suffix">
                    <input 
                      className="form-input"
                      type="tel"
                      value={viewingCustomer.alternateContactNumber || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <span className="input-suffix">☎</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => { setShowViewModal(false); setViewingCustomer(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Customer Confirmation Modal */}
      {showDeleteConfirm && customerToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-vehicle-title">Confirm Action</h3>
            </div>
            <div className="delete-vehicle-body">
              <p className="delete-vehicle-message">
                Are you sure you want to delete this Customer? This action cannot be undone.
              </p>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Customer Name:</span>
                  <span className="delete-detail-value">{customerToDelete.customerName}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleDeleteConfirm}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M6.5 4V3.5C6.5 2.94772 6.94772 2.5 7.5 2.5H8.5C9.05228 2.5 9.5 2.94772 9.5 3.5V4M12 4H4M11 4V13.5C11 14.0523 10.5523 14.5 10 14.5H6C5.44772 14.5 5 14.0523 5 13.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 7V11.5M9.5 7V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Customer Success Modal */}
      {showDeleteSuccess && deletedCustomerInfo && (
        <div className="success-modal-overlay" onClick={handleDeleteSuccessClose}>
          <div className="delete-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-success-header">
              <div className="delete-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-success-title">Success</h3>
              <button className="delete-success-close" onClick={handleDeleteSuccessClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', color: '#999', cursor: 'pointer', padding: '0', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✖</button>
            </div>
            <div className="delete-success-body">
              <p className="delete-success-message">Customer successfully deleted</p>
              <div className="delete-success-details">
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Customer Name:</span>
                  <span className="delete-success-detail-value">{deletedCustomerInfo.customerName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sample CSV Modal */}
      {showSampleCSVModal && (
        <div className="modal-overlay" onClick={() => setShowSampleCSVModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <div>Import Customers</div>
              <button className="action-btn" onClick={() => setShowSampleCSVModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Sample Excel Format</label>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>
                  Download the sample Excel template to see the required format for importing customers.
                  <br />
                  <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    Note: Fields marked with * are required. Dropdowns are available for State, City (Districts), and Country. Customer Code will be auto-generated if not provided.
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button 
                    className="btn-primary" 
                    onClick={handleDownloadSampleCSV}
                    style={{ flex: 1 }}
                  >
                    <span>⬇</span> Download Sample Excel
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSampleCSVModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleProceedWithImport}>
                Proceed with Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={handleImportCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
            <div className="modal-header">
              <div>Import Customers</div>
              <button className="action-btn" onClick={handleImportCancel}>✖</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#666' }}>File: <strong>{importFileName}</strong></p>
                <p style={{ margin: '0', fontSize: '0.85rem', color: '#999' }}>
                  {importValidCustomers.length} valid customer{importValidCustomers.length !== 1 ? 's' : ''} found.
                  {importInvalidRows.length > 0 && ` ${importInvalidRows.length} row${importInvalidRows.length !== 1 ? 's' : ''} will be skipped.`}
                </p>
              </div>

              {importInvalidRows.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#dc2626', margin: '0 0 12px 0' }}>Validation Summary</h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '8px' }}>
                    {importInvalidRows.map((item, idx) => (
                      <div key={idx} style={{ padding: '8px', fontSize: '0.85rem', borderBottom: idx < importInvalidRows.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <span style={{ color: '#666' }}>Row {item.row}:</span> <strong>{item.customerName}</strong> - <span style={{ color: '#dc2626' }}>{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importValidCustomers.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 12px 0' }}>Customers to Import</h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Code</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Name</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Mobile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importValidCustomers.slice(0, 10).map((c, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '8px' }}>{c.customerCode}</td>
                            <td style={{ padding: '8px' }}>{c.customerName}</td>
                            <td style={{ padding: '8px' }}>{c.mobileNumber}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importValidCustomers.length > 10 && (
                      <div style={{ padding: '8px', textAlign: 'center', fontSize: '0.85rem', color: '#666' }}>
                        ... and {importValidCustomers.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleImportCancel}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleImportConfirm}
                disabled={importValidCustomers.length === 0}
                style={{ opacity: importValidCustomers.length === 0 ? 0.5 : 1, cursor: importValidCustomers.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                Import {importValidCustomers.length > 0 && `(${importValidCustomers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Success Modal */}
      {showImportSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowImportSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Import Successful!</h2>
            <p className="success-message">
              {importSuccessCount} customer{importSuccessCount !== 1 ? 's' : ''} imported successfully.
              {importSkippedCount > 0 && ` ${importSkippedCount} row${importSkippedCount !== 1 ? 's' : ''} skipped.`}
            </p>
            <button className="success-ok-btn" onClick={() => setShowImportSuccess(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">Customer details have been successfully {successModalType === 'add' ? 'Added' : 'Updated'}</p>
            {successCustomerName && (
              <div className="success-id-badge">{successCustomerName}</div>
            )}
            <button className="success-ok-btn" onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyCustomerManagement;
